import React, {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import Accordion from "../accordian";
import ViewAttachmentModal from "../modal/ViewAttachmentModal";
import {
  allowedFileTypes,
  allowedImagesTypes,
  Attachments,
  ATTACHMENTS_TYPE,
  ContentType,
  DOC_TYPE_ID,
  PHOTO_NOTES,
  USER_ROLES_IDS,
  UserAppointment,
} from "../../constants/common";
import { useLocation } from "react-router-dom";
import { APP_ROUTES } from "../../routes";
import { useGetFileByType } from "../../hooks/visitor/mutation";
import toast from "react-hot-toast";
import CustomTooltip from "../tooltip";
import FileUploadInput from "../inputs/FileUploadInput";
import useAuthStore from "../../store/Auth";

interface AttachmentsProps {
  control: any;
  errors: any;

  uploadPhotoRef: React.MutableRefObject<HTMLInputElement | null>;
  photoFileName: string;
  setPhotoFileName: React.Dispatch<React.SetStateAction<string>>;
  setPhotoFileType: Dispatch<SetStateAction<string>>;

  uploadEidRef: React.MutableRefObject<HTMLInputElement | null>;
  eidFileName: string;
  setEidFileName: React.Dispatch<React.SetStateAction<string>>;
  setEidFileType: Dispatch<SetStateAction<string>>;

  uploadVisaRef?: React.MutableRefObject<HTMLInputElement | null>;
  visaFileName?: string;
  setVisaFileName?: React.Dispatch<React.SetStateAction<string>>;
  setVisaFileType?: Dispatch<SetStateAction<string>>;

  uploadPassportRef: MutableRefObject<HTMLInputElement | null>;
  passportFileName: string;
  setPassportFileName: Dispatch<SetStateAction<string>>;
  setPassportFileType: Dispatch<SetStateAction<string>>;

  uploadVehicleRegLicenseRef: MutableRefObject<HTMLInputElement | null>;
  setVehicleRegLicenseFileName: Dispatch<SetStateAction<string>>;
  setVehicleRegLicenseFileType: Dispatch<SetStateAction<string>>;
  vehicleRegLicenseFileName: string;

  uploadIqamaRef?: React.MutableRefObject<HTMLInputElement | null>;
  iqamaFileName?: string;
  setIqamaFileName?: React.Dispatch<React.SetStateAction<string>>;
  setIqamaFileType?: Dispatch<SetStateAction<string>>;

  uploadIdOtherRef?: React.MutableRefObject<HTMLInputElement | null>;
  idOtherFileName?: string;
  setIdOtherFileName?: React.Dispatch<React.SetStateAction<string>>;
  setIdOtherFileType?: Dispatch<SetStateAction<string>>;

  uploadHostEmployeeCardRef: MutableRefObject<HTMLInputElement | null>;
  hostEmployeeCardFileName: string;
  setHostEmployeeCardFileName: Dispatch<SetStateAction<string>>;
  setHostEmployeeCardFileType: Dispatch<SetStateAction<string>>;

  selectedIdType?: number;
  userAppointmentDetails?: UserAppointment;

  isVIP?: boolean;

  isAttachments?: Attachments;
  cardNumber: number;
  onAttachmentRemoved?: (info: { S_FileFieldName: string; s_FiledFileType: string }) => void;
}

const AttachmentsCard = ({
  control,
  errors,
  uploadPhotoRef,
  photoFileName,
  setPhotoFileName,
  setPhotoFileType,
  uploadEidRef,
  eidFileName,
  setEidFileName,
  setEidFileType,
  uploadPassportRef,
  passportFileName,
  setPassportFileName,
  setPassportFileType,

  uploadVisaRef,
  visaFileName,
  setVisaFileName,
  setVisaFileType,

  selectedIdType,
  userAppointmentDetails,
  isVIP,

  uploadIqamaRef,
  iqamaFileName,
  setIqamaFileName,
  setIqamaFileType,

  uploadIdOtherRef,
  idOtherFileName,
  setIdOtherFileName,
  setIdOtherFileType,

  uploadVehicleRegLicenseRef,
  setVehicleRegLicenseFileName,
  setVehicleRegLicenseFileType,
  vehicleRegLicenseFileName,

  uploadHostEmployeeCardRef,
  hostEmployeeCardFileName,
  setHostEmployeeCardFileName,
  setHostEmployeeCardFileType,

  isAttachments,
  cardNumber,
  onAttachmentRemoved,
}: AttachmentsProps) => {
  const [previewFiles, setPreviewFiles] = useState<
    Record<number, { url: string; contentType: string }>
  >({});

  const { t } = useTranslation();

  const user = useAuthStore((state) => state.user);

  const [selectedKey, setSelectedKey] = useState<string>("1");

  const [imageSrc, setImageSrc] = useState("");
  const [selectedType, setSelectedType] = useState(0);

  const [contentType, setContentType] = useState("");

  const { mutateAsync: GetFileByType, isPending: isGetFileByTypeLoading } =
    useGetFileByType();

  const [isViewAttachmentModalOpen, setIsViewAttachmentModalOpen] =
    useState(false);

  const location = useLocation();

  const isApprovalReq = useMemo(
    () => location.pathname === APP_ROUTES.APPROVE_REQUEST,
    [location.pathname]
  );

  const handleAttachmentView = useCallback(
    async (type: number) => {
      try {
        if (previewFiles && previewFiles[type]) {
          setImageSrc(previewFiles[type].url);
          setContentType(previewFiles[type].contentType);
          setIsViewAttachmentModalOpen(true);
          return;
        }

        if (userAppointmentDetails?.id) {
          setSelectedType(type);

          const body = {
            Id: userAppointmentDetails?.id,
            Type: type,
          };

          const res = await GetFileByType(body);

          if (res) {
            let url = "";
            if (res?.contentType !== ContentType.PDF) {
              url = `data:${res?.contentType};base64,${res?.photoFile}`;
            } else {
              const binaryString = atob(res?.photoFile);
              const byteArray = new Uint8Array(binaryString.length);

              for (let i = 0; i < binaryString.length; i++) {
                byteArray[i] = binaryString.charCodeAt(i);
              }

              const blob = new Blob([byteArray], { type: ContentType.PDF });
              url = URL.createObjectURL(blob);
            }
            setImageSrc(url);
            setContentType(res?.contentType);
            setIsViewAttachmentModalOpen(true);
          }
        }
      } catch (error) {
        toast.error(t("somethingWentWrong"));
      } finally {
        setSelectedType(0);
      }
    },
    [GetFileByType, previewFiles, t, userAppointmentDetails?.id]
  );

  return (
    <>
      <div className="bg-white p-4 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)] z-0">
        <Accordion
          selectedKey={selectedKey}
          onSelectionChange={setSelectedKey}
          title={
            <h1 className="text-xl md:text-2xl flex flex-1 items-center -mt-2">
              {t(`${cardNumber}`)}. {t("attachments")}
            </h1>
          }
        >
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 lg:gap-4">
            <FileUploadInput
              name="uploadPhoto"
              label={t("uploadPhoto")}
              accept=".png,.jpg,.jpeg"
              Tooltip={
                <CustomTooltip>
                  <p>{t("uploadPassportPhoto")}</p>
                  <p>{t("ensureClearImage")}</p>
                </CustomTooltip>
              }
              allowedTypes={allowedImagesTypes}
              isAttachments={isAttachments?.havePhoto}
              control={control}
              uploadRef={uploadPhotoRef}
              disabled={isApprovalReq}
              fileName={photoFileName}
              setFileName={setPhotoFileName}
              setFileType={setPhotoFileType}
              handleAttachmentView={handleAttachmentView}
              selectedType={selectedType}
              attachmentType={ATTACHMENTS_TYPE.PHOTO}
              isGetFileByTypeLoading={isGetFileByTypeLoading}
              error={errors.uploadPhoto}
              previewFiles={previewFiles}
              setPreviewFiles={setPreviewFiles}
              required={false}
              S_FileFieldName="S_PhotoUpload"
              s_FiledFileType="S_PhotoContentType"
              onRemoveAttachment={onAttachmentRemoved}
            />

            {selectedIdType === DOC_TYPE_ID.IQAMA && (
              <FileUploadInput
                name="uploadIqama"
                label={t("uploadIqama")}
                accept=".png,.jpg,.jpeg,.pdf"
                allowedTypes={allowedFileTypes}
                isAttachments={isAttachments?.haveIqama}
                control={control}
                uploadRef={uploadIqamaRef}
                disabled={isApprovalReq}
                fileName={iqamaFileName}
                setFileName={setIqamaFileName}
                setFileType={setIqamaFileType}
                handleAttachmentView={handleAttachmentView}
                selectedType={selectedType}
                attachmentType={ATTACHMENTS_TYPE.IQAMA}
                isGetFileByTypeLoading={isGetFileByTypeLoading}
                error={errors.uploadIqama}
                required={!isVIP}
                previewFiles={previewFiles}
                setPreviewFiles={setPreviewFiles}
                S_FileFieldName="S_IqamaUpload"
                s_FiledFileType="S_IqamaContentType"
                onRemoveAttachment={onAttachmentRemoved}
              />
            )}

            {
              // !selectedIdType ||
              selectedIdType === DOC_TYPE_ID.NATIONAL_ID && (
                <FileUploadInput
                  name="uploadEid"
                  label={t("uploadNationalID")}
                  accept=".png,.jpg,.jpeg,.pdf"
                  allowedTypes={allowedFileTypes}
                  isAttachments={isAttachments?.haveEid}
                  control={control}
                  uploadRef={uploadEidRef}
                  disabled={isApprovalReq}
                  fileName={eidFileName}
                  setFileName={setEidFileName}
                  setFileType={setEidFileType}
                  handleAttachmentView={handleAttachmentView}
                  selectedType={selectedType}
                  attachmentType={ATTACHMENTS_TYPE.EID}
                  isGetFileByTypeLoading={isGetFileByTypeLoading}
                  error={errors.uploadEid}
                  required={!isVIP}
                  previewFiles={previewFiles}
                  setPreviewFiles={setPreviewFiles}
                  S_FileFieldName="S_EIDFile"
                  s_FiledFileType="S_EIDContentType"
                  onRemoveAttachment={onAttachmentRemoved}
                />
              )
            }

            {selectedIdType === DOC_TYPE_ID.PASSPORT && (
              <FileUploadInput
                name="uploadPassport"
                label={t("uploadPassport")}
                accept=".png,.jpg,.jpeg,.pdf"
                allowedTypes={allowedFileTypes}
                isAttachments={isAttachments?.havePassport}
                control={control}
                uploadRef={uploadPassportRef}
                disabled={isApprovalReq}
                fileName={passportFileName}
                setFileName={setPassportFileName}
                setFileType={setPassportFileType}
                handleAttachmentView={handleAttachmentView}
                selectedType={selectedType}
                attachmentType={ATTACHMENTS_TYPE.PASSPORT}
                isGetFileByTypeLoading={isGetFileByTypeLoading}
                error={errors.uploadPassport}
                required={!isVIP}
                previewFiles={previewFiles}
                setPreviewFiles={setPreviewFiles}
                S_FileFieldName="S_PassportFile"
                s_FiledFileType="S_PassportContentType"
                onRemoveAttachment={onAttachmentRemoved}
              />
            )}

            {selectedIdType === DOC_TYPE_ID.OTHER && (
              <FileUploadInput
                name="uploadIdOther"
                label={t("uploadDocumentOther")}
                accept=".png,.jpg,.jpeg,.pdf"
                allowedTypes={allowedFileTypes}
                isAttachments={isAttachments?.haveOthers}
                control={control}
                uploadRef={uploadIdOtherRef}
                disabled={isApprovalReq}
                fileName={idOtherFileName}
                setFileName={setIdOtherFileName}
                setFileType={setIdOtherFileType}
                handleAttachmentView={handleAttachmentView}
                selectedType={selectedType}
                attachmentType={ATTACHMENTS_TYPE.OTHERS}
                isGetFileByTypeLoading={isGetFileByTypeLoading}
                error={errors.uploadIdOther}
                required={!isVIP}
                previewFiles={previewFiles}
                setPreviewFiles={setPreviewFiles}
                S_FileFieldName="S_OthersUpload"
                s_FiledFileType="S_OthersContentType"
                onRemoveAttachment={onAttachmentRemoved}
              />
            )}

            {selectedIdType === DOC_TYPE_ID.VISA && (
              <FileUploadInput
                name="uploadVisa"
                label={t("uploadVisa")}
                accept=".png,.jpg,.jpeg,.pdf"
                allowedTypes={allowedFileTypes}
                isAttachments={isAttachments?.haveVisa}
                control={control}
                uploadRef={uploadVisaRef}
                disabled={isApprovalReq}
                fileName={visaFileName}
                setFileName={setVisaFileName}
                setFileType={setVisaFileType}
                handleAttachmentView={handleAttachmentView}
                selectedType={selectedType}
                attachmentType={ATTACHMENTS_TYPE.VISA}
                isGetFileByTypeLoading={isGetFileByTypeLoading}
                error={errors.uploadVisa}
                required={!isVIP}
                previewFiles={previewFiles}
                setPreviewFiles={setPreviewFiles}
                S_FileFieldName="S_VisaFile"
                s_FiledFileType="S_VisaContentType"
                onRemoveAttachment={onAttachmentRemoved}
              />
            )}

            <FileUploadInput
              name="uploadVehicleRegLicense"
              label={t("vehicleRegistrationLicense")}
              accept=".png,.jpg,.jpeg,.pdf"
              allowedTypes={allowedFileTypes}
              isAttachments={isAttachments?.haveVehicleRegistration}
              control={control}
              uploadRef={uploadVehicleRegLicenseRef}
              disabled={isApprovalReq}
              fileName={vehicleRegLicenseFileName}
              setFileName={setVehicleRegLicenseFileName}
              setFileType={setVehicleRegLicenseFileType}
              handleAttachmentView={handleAttachmentView}
              selectedType={selectedType}
              attachmentType={ATTACHMENTS_TYPE.VEHICLE}
              isGetFileByTypeLoading={isGetFileByTypeLoading}
              required={false}
              previewFiles={previewFiles}
              setPreviewFiles={setPreviewFiles}
              S_FileFieldName="S_VehicleRegistrationFile"
              s_FiledFileType="S_VehicleRegistrationContentType"
              onRemoveAttachment={onAttachmentRemoved}
            />

            <FileUploadInput
              name="uploadHostEmployeeCard"
              label={t("hostEmployeeCard")}
              accept=".png,.jpg,.jpeg,.pdf"
              allowedTypes={allowedFileTypes}
              isAttachments={isAttachments?.haveHostEmployeeCard}
              control={control}
              disabled={user?.n_RoleID !== USER_ROLES_IDS.STAFF}
              uploadRef={uploadHostEmployeeCardRef}
              fileName={hostEmployeeCardFileName}
              setFileName={setHostEmployeeCardFileName}
              setFileType={setHostEmployeeCardFileType}
              handleAttachmentView={handleAttachmentView}
              selectedType={selectedType}
              attachmentType={ATTACHMENTS_TYPE.HOST_EMPLOYEE_CARD}
              isGetFileByTypeLoading={isGetFileByTypeLoading}
              error={errors.uploadHostEmployeeCard}
              required
              previewFiles={previewFiles}
              setPreviewFiles={setPreviewFiles}
              S_FileFieldName="S_HostEmployeeCardFile"
              s_FiledFileType="S_HostEmployeeCardContentType"
              onRemoveAttachment={onAttachmentRemoved}
            />
          </div>
          <p className="text-red-600 text-xs mt-4">
            <span className="font-semibold">{t("note")}:</span>{" "}
            <ul className="list-disc list-inside">
              {PHOTO_NOTES.map((key) => (
                <li key={key}>{t(key)}</li>
              ))}
            </ul>
          </p>
        </Accordion>
      </div>

      <ViewAttachmentModal
        attachment={imageSrc}
        contentType={contentType}
        isOpen={isViewAttachmentModalOpen}
        onOpenChange={() => {
          setIsViewAttachmentModalOpen(false);
          setImageSrc("");
        }}
      />
    </>
  );
};

export default React.memo(AttachmentsCard);
